import { useState, useRef, useEffect } from 'react';

export interface FAQItem {
  q: string;
  a: string;
}

interface FAQSectionProps {
  items: FAQItem[];
  heading?: string;
}

export default function FAQSection({
  heading = 'Frequently Asked Questions',
  items,
}: FAQSectionProps) {
  const [open, setOpen] = useState<number | null>(0);
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className="py-24 md:py-32 px-6 md:px-12 lg:px-16"
      style={{ background: 'linear-gradient(180deg, #0d0d0d 0%, #080808 100%)' }}
      ref={ref}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-14 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-[1px] bg-[#C9A063]" />
            <span className="text-[#C9A063] text-[11px] font-sans font-medium tracking-[0.3em] uppercase">FAQ</span>
            <div className="w-8 h-[1px] bg-[#C9A063]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white">{heading}</h2>
        </div>

        <div className="space-y-3">
          {items.map((item, i) => (
            <div
              key={i}
              className={`bg-[#111111] border rounded-2xl overflow-hidden transition-all duration-700 ${
                open === i ? 'border-[#C9A063]/30' : 'border-white/6'
              } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <button
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full flex items-center justify-between px-7 py-5 text-left cursor-pointer group"
              >
                <span
                  className={`font-sans font-medium text-base leading-snug pr-6 transition-colors duration-200 ${
                    open === i ? 'text-[#C9A063]' : 'text-white/75 group-hover:text-white'
                  }`}
                >
                  {item.q}
                </span>
                <div
                  className={`w-7 h-7 flex items-center justify-center rounded-full border shrink-0 transition-all duration-300 ${
                    open === i
                      ? 'border-[#C9A063] bg-[#C9A063]/10 rotate-45'
                      : 'border-white/15 bg-transparent'
                  }`}
                >
                  <i className="ri-add-line text-[#C9A063] text-base"></i>
                </div>
              </button>

              <div
                className={`overflow-hidden transition-all duration-500 ${
                  open === i ? 'max-h-[300px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-7 pb-6 border-t border-white/5">
                  <p className="text-white/50 font-sans text-sm leading-relaxed pt-5">{item.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
